import { useEffect } from 'react'
import { useUser } from '@clerk/clerk-react'
import { useStore } from '../store/useStore'
import { usePartyKit } from '../hooks/usePartyKit'

/**
 * CollaborationProvider - Manages PartyKit WebSocket connection for social coding
 * Renders nothing visually, just handles real-time sync when in a room
 */
export default function CollaborationProvider() {
  const { user, isSignedIn, isLoaded } = useUser()
  const { currentRoomId, collaborationMode, setCollaborators } = useStore()
  
  // Only connect to PartyKit when Clerk loaded, signed in, in group mode with valid room
  // Gracefully handle Clerk timeout on satellite domain
  const shouldConnect = isLoaded && isSignedIn && collaborationMode === 'group' && currentRoomId
  
  // Use the PartyKit hook - it handles connection internally
  const { isConnected, users, sendFileUpdate, sendPrompt } = usePartyKit(
    shouldConnect ? currentRoomId : null,
    shouldConnect ? user : null
  )
  
  // Sync collaborators list to store when users change
  useEffect(() => {
    if (shouldConnect && users.length > 0) {
      setCollaborators(users)
    }
  }, [users, shouldConnect, setCollaborators])
  
  // Expose sendFileUpdate and sendPrompt globally via custom events
  useEffect(() => {
    if (!shouldConnect || !isConnected) return
    
    const handleFileChange = (e) => {
      const { fileName, content } = e.detail
      sendFileUpdate(fileName, content)
    }
    
    const handlePromptSend = (e) => {
      const { prompt } = e.detail
      sendPrompt(prompt)
    }
    
    window.addEventListener('collaboration:file-change', handleFileChange)
    window.addEventListener('collaboration:prompt', handlePromptSend)
    
    return () => {
      window.removeEventListener('collaboration:file-change', handleFileChange)
      window.removeEventListener('collaboration:prompt', handlePromptSend)
    }
  }, [shouldConnect, isConnected, sendFileUpdate, sendPrompt])
  
  // Log connection status for debugging
  useEffect(() => {
    if (shouldConnect) {
      console.log(`[CollaborationProvider] Room ${currentRoomId}: ${isConnected ? 'connected' : 'connecting...'}`)
    }
  }, [shouldConnect, currentRoomId, isConnected])
  
  // This component renders nothing
  return null
}
